import type { PendoIdentity } from '@klareospace/pharos-lib-front-utils';
import { toPendoIdentity } from '@klareospace/pharos-lib-front-utils';
import * as React from 'react';
import { useEffect } from 'react';

import type { PendoClient } from './create-pendo-client';

export interface PendoBridgeProps {
  children: React.ReactNode;
  /** Klareo JWT string, or null when the user is unauthenticated. Provided by the consuming app. */
  klareoToken: string | null;
  /** Pendo client to identify/clear with the decoded identity. */
  pendoClient: PendoClient;
}

/**
 * Identifies or clears the Pendo session from the Klareo JWT.
 *
 * Receives the current `klareoToken` via the `klareoToken` prop, decodes it via
 * `toPendoIdentity`, and calls `pendoClient.identify` whenever a valid identity is derived,
 * or `pendoClient.clearSession` (e.g. on logout) otherwise.
 */
export function PendoBridge({ children, klareoToken, pendoClient }: PendoBridgeProps): React.JSX.Element {
  useEffect((): void => {
    const identity: PendoIdentity | null = toPendoIdentity(klareoToken);
    if (identity) {
      pendoClient.identify(identity);
    } else {
      pendoClient.clearSession();
    }
  }, [klareoToken, pendoClient]);

  return <>{children}</>;
}
